// Panel „Rede mit der Didaktik-Assistenz" — dialogische Beratung im Wizard
// (Steps 2–4, unter den Karten, über der Navigation) und seit Block 3 auch
// im Kurs-Kontext (Audit-Report, „Mit der KI besprechen").
//
// Die Assistenz sieht bei jeder Nachricht den aktuellen Stand (Wizard:
// Entwurf aus den Props; Kurs-Kontext: der Server lädt den persistierten
// Kurs über course_id selbst) und kann optional ein `proposal` (Tool
// propose_changes) zurückgeben: eine komplett überarbeitete Liste für EINE
// Kategorie, die als Karte mit „Übernehmen"/„Verwerfen" gerendert wird.
// Was „Übernehmen" tut, entscheidet der Parent über onApply (Wizard-State
// ersetzen bzw. per PATCH/POST in den Kurs schreiben).
//
// History und hängender Vorschlag liegen im Parent-State, damit sie
// Step-Wechsel überstehen (Wizard ist stateless, kein Server-State).
// `seedInput` füllt das Eingabefeld vor (z. B. Flaw-Text aus dem Audit).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  api,
  APIError,
  isQuotaError,
  type ChatMessage,
  type ChatProposal,
  type ChatRequestPayload,
  type ContentLang,
  type SuggestedAT,
  type SuggestedILO,
  type SuggestedTLA,
  type WizardCourseMeta,
} from "@/api/client";
import CreditCost from "@/components/CreditCost";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";

interface DidaktikChatProps {
  // "wizard" (Default): Entwurfsstand kommt per Props mit.
  // "course" (Block 3): der Server lädt den Kurs über courseId selbst.
  context?: "wizard" | "course";
  courseId?: string;
  // Vorbefüllter Eingabetext (z. B. Befund + Empfehlung aus dem Audit).
  seedInput?: string;
  step?: 2 | 3 | 4;
  courseMeta?: WizardCourseMeta;
  ilos?: SuggestedILO[];
  tlas?: SuggestedTLA[];
  ats?: SuggestedAT[];
  strategy?: string;
  language: ContentLang;
  messages: ChatMessage[];
  setMessages: (m: ChatMessage[]) => void;
  proposal: ChatProposal | null;
  setProposal: (p: ChatProposal | null) => void;
  onApply: (p: ChatProposal) => void;
  disabled?: boolean;
}

const TARGET_LABEL_KEYS: Record<ChatProposal["target"], string> = {
  ilos: "chat.targetIlos",
  tlas: "chat.targetTlas",
  ats: "chat.targetAts",
};

// Kompakte Vorschau: die ersten ~3 Texte des Vorschlags (ILO: text,
// TLA/AT: description).
function previewTexts(p: ChatProposal): string[] {
  return p.items
    .map((it) => ("text" in it ? it.text : it.description))
    .filter((s) => !!s)
    .slice(0, 3);
}

export default function DidaktikChat(props: DidaktikChatProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);
  // Recherche-Toggle (Default AUS, metered): gilt pro Nachricht und wird
  // als enable_research mitgeschickt.
  const [research, setResearch] = useState(false);

  const context = props.context ?? "wizard";

  // Vorbefüllung (z. B. „Mit der KI besprechen" kopiert den Flaw-Text rein).
  useEffect(() => {
    if (props.seedInput) setInput(props.seedInput);
  }, [props.seedInput]);

  async function send() {
    const text = input.trim();
    if (!text || busy || props.disabled) return;
    const next: ChatMessage[] = [...props.messages, { role: "user", content: text }];
    props.setMessages(next);
    setInput("");
    setBusy(true);
    setError(null);
    try {
      const payload: ChatRequestPayload =
        context === "course"
          ? {
              messages: next,
              context: "course",
              course_id: props.courseId,
              language: props.language,
              enable_research: research,
            }
          : {
              messages: next,
              step: props.step,
              course_meta: props.courseMeta,
              ilos: props.ilos,
              tlas: props.tlas,
              ats: props.ats,
              strategy: props.strategy,
              language: props.language,
              enable_research: research,
            };
      const res = await api.chatMessage(payload);
      props.setMessages([...next, {
        role: "assistant",
        content: res.reply,
        sources: res.sources ?? [],
        research,
      }]);
      props.setProposal(res.proposal ?? null);
    } catch (e) {
      if (isQuotaError(e)) {
        setQuota(e.data);
      } else if (e instanceof APIError && e.status === 429) {
        setError(t("chat.rateLimited"));
      } else if (e instanceof APIError) {
        setError(e.detail);
      } else {
        setError(t("chat.failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  const proposal = props.proposal;
  const preview = proposal ? previewTexts(proposal) : [];

  return (
    <section className="mt-10 bg-ca-card p-6 rounded-xl border border-ca-surface-border">
      <h3 className="text-xl font-serif font-semibold text-ca-surface-ink mb-1">
        {t("chat.title")}
      </h3>
      <p className="text-sm text-ca-surface-muted mb-4">{t("chat.intro")}</p>

      {props.messages.length > 0 && (
        <div className="space-y-3 mb-4 max-h-80 overflow-y-auto pr-1">
          {props.messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
              <div
                className={`inline-block max-w-[85%] px-4 py-2 rounded-lg text-[15px] whitespace-pre-wrap text-left ${
                  m.role === "user"
                    ? "bg-ca-accent/15 text-ca-surface-ink"
                    : "bg-white border border-ca-surface-border text-ca-surface-ink"
                }`}
              >
                {m.content}
              </div>
              {m.role === "assistant" && m.research && (
                m.sources && m.sources.length > 0 ? (
                  <div className="mt-1 ml-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-ca-surface-muted">
                      {t("chat.sources")}
                    </p>
                    <ul className="text-sm space-y-0.5">
                      {m.sources.map((s, j) => (
                        <li key={j}>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-ca-surface-accent underline hover:text-ca-accent transition"
                          >
                            {s.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="mt-1 ml-1 text-xs text-ca-surface-muted italic">
                    {t("chat.noSources")}
                  </p>
                )
              )}
            </div>
          ))}
        </div>
      )}
      {busy && (
        <p className="mb-4 text-sm text-ca-surface-muted">{t("chat.sending")}</p>
      )}

      {proposal && (
        <div className="mb-4 p-4 rounded-lg border-2 border-ca-accent bg-ca-accent/5">
          <p className="text-sm font-bold uppercase tracking-wider text-ca-surface-accent mb-1">
            {t("chat.proposalTitle", { target: t(TARGET_LABEL_KEYS[proposal.target]) })}
          </p>
          {proposal.rationale && (
            <p className="text-[15px] text-ca-surface-ink mb-2">{proposal.rationale}</p>
          )}
          {preview.length > 0 && (
            <ul className="text-sm text-ca-surface-muted list-disc pl-5 mb-3 space-y-1">
              {preview.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
              {proposal.items.length > preview.length && (
                <li>{t("chat.proposalMore", { count: proposal.items.length - preview.length })}</li>
              )}
            </ul>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => props.onApply(proposal)}
              className="px-4 py-2 bg-ca-accent text-ca-brand text-sm font-bold rounded-md hover:bg-brand-gold-dark transition"
            >
              {t("chat.apply")}
            </button>
            <button
              type="button"
              onClick={() => props.setProposal(null)}
              className="px-4 py-2 border border-ca-surface-border text-ca-surface-muted text-sm font-semibold rounded-md hover:text-ca-surface-ink transition"
            >
              {t("chat.discard")}
            </button>
          </div>
          <p className="text-xs text-ca-surface-muted mt-2 italic">
            {context === "course" ? t("chat.applyHintCourse") : t("chat.applyHint")}
          </p>
        </div>
      )}

      {quota !== null && (
        <div className="mb-4">
          <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
        </div>
      )}
      {error && <p className="mb-2 text-sm text-verdict-red">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setResearch((r) => !r)}
          disabled={busy || props.disabled}
          aria-pressed={research}
          title={t("chat.researchHint")}
          className={`self-end px-3 py-2 border rounded-md text-sm font-semibold transition disabled:opacity-40 ${
            research
              ? "border-ca-accent bg-ca-accent/15 text-ca-surface-accent"
              : "border-ca-surface-border text-ca-surface-muted hover:text-ca-surface-ink"
          }`}
        >
          <span aria-hidden>🌐</span> {t("chat.research")}
        </button>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder={t("chat.placeholder")}
          rows={2}
          disabled={busy || props.disabled}
          className="flex-1 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-sm resize-y"
        />
        <span className="self-end pb-2 text-sm">
          <CreditCost feature="chat" />
        </span>
        <button
          type="button"
          onClick={() => void send()}
          disabled={!input.trim() || busy || props.disabled}
          className="self-end px-5 py-2 bg-ca-accent text-ca-brand text-sm font-bold rounded-md hover:bg-brand-gold-dark transition disabled:opacity-40"
        >
          {busy ? t("chat.sending") : t("chat.send")}
        </button>
      </div>
    </section>
  );
}
